import {
  changeSignificance,
  fieldSignificance,
  hasFieldMeanings,
  type FieldMeaningBindings,
} from '../issues/significance';
import { issueFieldLabel } from '../issues/fields';
import type { NotificationBrief } from './brief';
import type { NotificationDisplayEntry } from './types';

export type NotificationVerdict = {
  entryKey: string;
  eventIds: string[];
  /** Plain labels of the details the writer judged, never raw source keys. */
  labels: string[];
  reason: string;
  /** Whether the platform let the writer's "not worth telling" stand. */
  honored: boolean;
};

/**
 * The writer may call an update noise only when every material change in it is
 * an edit to a detail no dictionary entry explains. Catalogued details, rows
 * added or removed, status changes, new or unlisted records and coverage
 * changes are always sent: the dictionary, not the model, holds that authority.
 */
export function modelMaySilence(
  entry: NotificationDisplayEntry,
  bindings?: FieldMeaningBindings,
): {
  allowed: boolean;
  labels: string[];
} {
  const refused = (labels: string[] = []) => ({ allowed: false, labels });
  // Every transition but the bare "record changed" marker is news in itself: a
  // status moved, a record appeared or vanished, or coverage changed.
  const news = entry.transitions.some((transition) => transition !== 'record_changed');
  if (entry.issueIdentity === null || news || !entry.before || !entry.after) return refused();
  const scope = { source: entry.source, recordKind: entry.recordKind };
  const { materialDeltas } = changeSignificance(entry.before, entry.after, scope, bindings);
  if (!materialDeltas.length) return refused();
  const labels = [...new Set(materialDeltas.map((delta) => issueFieldLabel(delta.field)))];
  // A source no skill has described is sent whole: without a declaration there is
  // no fence, so nothing in it is the model's to judge.
  if (!hasFieldMeanings(entry.source, bindings)) return refused(labels);
  // Only a plain, undeclared detail is the model's to judge: a label whose shape
  // already says date, amount or status is news the dictionary simply has not met.
  const uncatalogued = materialDeltas.every((delta) => {
    if (delta.rows || delta.structural) return false;
    const significance = fieldSignificance(delta.field, scope, bindings);
    return significance.material && !significance.catalogued && significance.kind === 'detail';
  });
  return { allowed: uncatalogued, labels };
}

/**
 * Record every "not worth telling" the writer gave and whether the fence lets
 * it stand. A batch goes silent only when every frozen entry, not merely every
 * highlighted update, carries an honored verdict; an entry the writer never saw
 * keeps the email going out as written, so subject and body always agree.
 */
export function applyNotificationVerdicts(
  brief: NotificationBrief,
  entries: NotificationDisplayEntry[],
  bindings?: FieldMeaningBindings,
): { verdicts: NotificationVerdict[]; silent: boolean } {
  const verdicts: NotificationVerdict[] = [];
  for (const property of brief.properties)
    for (const update of property.updates) {
      if (update.worthTelling !== false) continue;
      const entry = entries.find((candidate) => candidate.key === update.entryKey);
      if (!entry) continue;
      const fence = modelMaySilence(entry, bindings);
      verdicts.push({
        entryKey: update.entryKey,
        eventIds: entry.eventIds,
        labels: fence.labels,
        reason: update.reason ?? '',
        honored: fence.allowed,
      });
    }
  const honored = new Set(verdicts.filter((verdict) => verdict.honored).map((v) => v.entryKey));
  const silent =
    brief.writer === 'ai' && entries.length > 0 && entries.every((entry) => honored.has(entry.key));
  return { verdicts, silent };
}
